import { LinkedApplicationsField } from '@/pages/Subscriptions/Forms/components/LinkedApplicationsField'
import { ApplicationAccessBlock, ProjectAccessTicket } from '@/pages/Subscriptions/Requests/types'
import { Button } from '@/components/shadcn/button'
import { useRestClient } from '@/contexts/QueryProvider'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

type ProjectAccessRequestSectionProps = {
  projectId: number
  organisationId: number
  onSubmitted?: () => void
}

export function ProjectAccessRequestSection({
  projectId,
  organisationId,
  onSubmitted,
}: ProjectAccessRequestSectionProps) {
  const {
    getSubscriptionsApiListOrganisationUsers,
    getApplicationsApiGetApplications,
    putRequestsApiAddRequest,
    getRequestsApiGetOrganisationRequests,
  } = useRestClient()

  const { data: users } = getSubscriptionsApiListOrganisationUsers.useQuery({ query: { orgId: organisationId } })
  const { data: allApplications } = getApplicationsApiGetApplications.useQuery(undefined)

  const userOptions = useMemo(
    () =>
      users?.map((u) => ({
        value: u.id!,
        label: `${u.givenName} ${u.familyName}`,
        description: u.email,
      })) ?? [],
    [users]
  )

  const applicationOptions = useMemo(
    () =>
      allApplications?.map((a) => ({
        value: a.applicationId!,
        label: a.appName ?? `App ${a.applicationId}`,
      })) ?? [],
    [allApplications]
  )

  const { mutate, isPending } = putRequestsApiAddRequest.useMutation(undefined, {
    onSuccess: () => {
      getRequestsApiGetOrganisationRequests.invalidateQueries({ parameters: { query: { orgId: organisationId } } })
      toast.success('Access request submitted')
      onSubmitted?.()
    },
    onError: () => toast.error('Failed to submit access request'),
  })

  const [linkedApplications, setLinkedApplications] = useState<ApplicationAccessBlock[]>([])

  const handleSubmit = () => {
    const ticket: ProjectAccessTicket = {
      projectId,
      applications: linkedApplications,
    }
    mutate({
      body: {
        ticketType: 1,
        organisationId,
        ticket: JSON.stringify(ticket),
      },
    })
  }

  return (
    <div className="pt-6">
      <div className="space-y-3 border-t pt-5">
        <div>
          <h3 className="text-sm font-medium">Request workspace access</h3>
          <p className="text-sm text-muted-foreground">
            Submit a ticket for Databricks notebooks, catalogue, or other linked application access on this project.
          </p>
        </div>
        <LinkedApplicationsField
          applicationOptions={applicationOptions}
          userOptions={userOptions}
          value={linkedApplications}
          onChange={setLinkedApplications}
        />
        <Button
          type="button"
          variant="outline"
          disabled={isPending || linkedApplications.length === 0}
          onClick={handleSubmit}
        >
          Submit access request
        </Button>
      </div>
    </div>
  )
}
